import { Component, inject, signal, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { MiddlewareApi } from '../../core/services/middleware-api.service';
import { CognitoGroup, CognitoUser } from '../../core/models';

@Component({
    selector: 'app-users',
    standalone: true,
    imports: [FormsModule, TableModule, CardModule, TagModule, ButtonModule, SelectModule, TooltipModule],
    template: `
        <p-card header="Users & Roles">
            @if (error()) {
                <p class="text-red-500 mb-3">{{ error() }}</p>
            }
            <p-table [value]="users()" [loading]="loading()">
                <ng-template pTemplate="header">
                    <tr>
                        <th>User</th>
                        <th>Status</th>
                        <th>Roles<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="Click a role to remove it from the user." tooltipPosition="top"></i></th>
                        <th style="width: 18rem">Assign role</th>
                    </tr>
                </ng-template>
                <ng-template pTemplate="body" let-user>
                    <tr>
                        <td>{{ user.email ?? user.username }}</td>
                        <td><p-tag [value]="user.status" [severity]="user.status === 'CONFIRMED' ? 'success' : 'secondary'" /></td>
                        <td>
                            @for (g of user.groups; track g) {
                                <p-tag [value]="g" severity="info" class="mr-1 mb-1 cursor-pointer" (click)="remove(user, g)" />
                            }
                            @if (!user.groups.length) {
                                <span class="text-surface-500">none</span>
                            }
                        </td>
                        <td>
                            <div class="flex gap-2">
                                <p-select
                                    [options]="groupOptions()"
                                    [(ngModel)]="selected[user.username]"
                                    optionLabel="label"
                                    optionValue="value"
                                    placeholder="Role"
                                    appendTo="body"
                                    styleClass="w-40"
                                />
                                <p-button icon="pi pi-plus" [disabled]="!selected[user.username]" (onClick)="add(user)" />
                            </div>
                        </td>
                    </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                    <tr>
                        <td colspan="4">{{ error() ?? 'No users found.' }}</td>
                    </tr>
                </ng-template>
            </p-table>
        </p-card>
    `
})
export class Users implements OnInit {
    private readonly api = inject(MiddlewareApi);
    readonly users = signal<CognitoUser[]>([]);
    readonly groups = signal<CognitoGroup[]>([]);
    readonly loading = signal(true);
    readonly error = signal<string | null>(null);
    selected: Record<string, string> = {};

    ngOnInit(): void {
        this.load();
        this.api.getGroups().subscribe({ next: (g) => this.groups.set(g), error: () => {} });
    }

    load(): void {
        this.loading.set(true);
        this.api.getUsers().subscribe({
            next: (u) => {
                this.users.set(u);
                this.loading.set(false);
            },
            error: (e) => {
                this.error.set(this.describe(e));
                this.loading.set(false);
            }
        });
    }

    groupOptions() {
        return this.groups().map((g) => ({ label: g.name, value: g.name }));
    }

    add(user: CognitoUser): void {
        const g = this.selected[user.username];
        if (!g) return;
        this.api.addUserToGroup(user.username, g).subscribe({
            next: () => {
                this.selected[user.username] = '';
                this.load();
            },
            error: (e) => this.error.set(this.describe(e))
        });
    }

    remove(user: CognitoUser, group: string): void {
        this.api.removeUserFromGroup(user.username, group).subscribe({
            next: () => this.load(),
            error: (e) => this.error.set(this.describe(e))
        });
    }

    private describe(e: any): string {
        if (e?.status === 0) return 'Cannot reach the API.';
        if (e?.status === 401) return 'Unauthorized — sign in.';
        if (e?.status === 403) return 'Forbidden — you need the admin role.';
        if (e?.status === 500) return 'Server error — check the backend Cognito IAM permissions / UserPoolId config.';
        return `Error ${e?.status ?? ''}: ${e?.message ?? 'request failed'}`;
    }
}
